import { NgSignaturePadOptions, SignaturePadComponent } from "@almothafar/angular-signature-pad";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from "@angular/core";
import { ConceptType, PersonRole } from "@ha/data/basic";
import { BaseModalComponent } from "@ha/ui/common";
import { validatePnr } from "@ha/util/common";
import { TranslocoService } from "@ngneat/transloco";
import { untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionSuccessful, Store } from "@ngxs/store";

import { PhotoRequirement } from "@ha/data/basic";
import { PermitTeamActions } from "../../../permit-team/states/permit-team.action";
import { UserAgreementActions } from "../../../user-agreement/states/user-agreement.actions";
import { PermitPerson } from "../../models/permit-person.model";

export interface PermitCreateSignPersonModalComponentData {
    permitId: number;
    roles: PersonRole[];
    person: PermitPerson;
    isDemo: boolean;
    disabledFireAlarm: string;
    hasDisabledFireAlarm?: boolean | null;
    endPostWatchSignature: boolean;
    permitEndCommentRequired: boolean;
    permitEndPhotoRequirement: PhotoRequirement;
}

@Component({
    templateUrl: "permit-create-sign-person-modal.component.html",
    styleUrls: ["permit-create-sign-person-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitCreateSignPersonModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    @ViewChild("signature")
    public signaturePad: SignaturePadComponent;

    public signaturePadOptions: NgSignaturePadOptions = undefined;

    public data: PermitCreateSignPersonModalComponentData;

    public hasSignature = false;

    public agreementAccepted = false;

    public isPersonalNumberValid = true;

    public hasForeignPersonalNumber = false;

    public personalNumber = "";

    public PersonRole = PersonRole;

    public get agrementAcceptedBefore() {
        return this.data.person.hasAcceptedUserAgreement || this.data.roles.includes(PersonRole.Superintendent);
    }

    public get isValid() {
        return this.hasSignature
            && ((this.data.person.concept != 2
                && (this.agrementAcceptedBefore || this.agreementAccepted || this.data.isDemo))
                || (this.data.person.concept == 2
                    && (this.data.person.personalNumber != "" || this.data.person.hasForeignPersonalNumber == true)
                    && (this.agrementAcceptedBefore || this.agreementAccepted)));
    }

    public get formattedRoles(): string {
        return this.data.roles.map((role) =>
            this.translocoService.translate("permit.signature.roles." + PersonRole[role].toLowerCase())
        ).join(", ");
    }

    constructor(
        private store: Store,
        private actions$: Actions,
        private cdr: ChangeDetectorRef,
        private translocoService: TranslocoService,
    ) {
        super();
    }

    public ngOnInit() {
        this.actions$.pipe(ofActionSuccessful(UserAgreementActions.AcceptUserAgreementOnIssuerUnit)).pipe(
            untilDestroyed(this),
        ).subscribe(() => {
            this.agreementAccepted = true;
            this.cdr.markForCheck();
        });

        this.actions$.pipe(ofActionSuccessful(UserAgreementActions.RejectAgreementOnIssuerUnit)).pipe(
            untilDestroyed(this),
        ).subscribe(() => {
            this.agreementAccepted = false;
            this.cdr.markForCheck();
        });
    }

    public ngAfterViewInit(): void {
        this.getScreenSize();
        this.signaturePad.clear(); // invoke functions from szimek/signature_pad API
    }

    @HostListener("window:resize")
    public getScreenSize() {
        let width = 400;
        if (window.innerWidth < width) {
            width = window.innerWidth;
        }

        this.signaturePadOptions = {
            minWidth: 1,
            maxWidth: 4,
            backgroundColor: "#ffffff",
            canvasWidth: width,
            canvasHeight: width / 2,
        };

        this.cdr.detectChanges();
    }

    public clear() {
        this.signaturePad.clear();
        this.hasSignature = !this.signaturePad.isEmpty();
    }

    public openAgreement(event: MouseEvent): void {
        event.preventDefault();
        const isExternal = this.data.person.concept !== ConceptType.HetaArbeten;
        this.store.dispatch(new UserAgreementActions.OpenUserAgreementOnIssuerUnit(isExternal));
    }

    public hasForeginPersonalNumberClick(): void {
        this.personalNumber = "";
        this.isPersonalNumberValid = true;
    }

    public personalNumberOnChange(): void {
        this.hasForeignPersonalNumber = false;
    }

    public save() {
        if (this.personalNumber !== "") {
            this.isPersonalNumberValid = validatePnr(this.personalNumber);
        }

        if (this.isValid && this.isPersonalNumberValid === true) {
            this.store.dispatch(
                new PermitTeamActions.SignPermitWithSignature({
                    permitId: this.data.permitId,
                    personId: this.data.person.personId,
                    role: undefined,
                    roles: this.data.roles,
                    signatureData: this.signaturePad.toDataURL(),
                    endPostWatchSignature: this.data.endPostWatchSignature,
                    personalNumber: this.personalNumber,
                    hasForeignPersonalNumber: this.hasForeignPersonalNumber,
                    comment: undefined,
                }),
            );
            this.closeModal();
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public drawComplete(event: MouseEvent | Touch): void {
        this.hasSignature = !this.signaturePad.isEmpty();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
    public drawStart(event: MouseEvent | Touch): void {
    }
}
